/**
 * Paying or saving a card with Stripe.js: a saved card in one tap, or a new one in
 * Stripe's Payment Element (cards, Apple Pay, Google Pay). Card details go straight to
 * Stripe (or the payments stub while testing), never through our servers.
 */

import { get } from "@taxi/mobile-web/api";
import { useResource } from "@taxi/mobile-web/hooks";
import { CardIcon, PlusIcon } from "@taxi/mobile-web/icons";
import { Choice, ChoiceGroup, Notice, Spinner } from "@taxi/mobile-web/ui";
import { type Ref, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useSession } from "../lib/session";
import {
  appearance,
  type Elements,
  loadStripe,
  type PaymentElement,
  type Stripe,
  stripeMessage,
} from "../lib/stripe";
import type { PaymentSheet, SavedCard } from "../lib/types";

export type PaymentFormHandle = {
  /** Pays (or saves the card). Resolves to an error message, or null when it went through. */
  confirm(): Promise<string | null>;
};

const brandName = (brand: string) => brand.charAt(0).toUpperCase() + brand.slice(1);

export function PaymentForm({
  sheet,
  returnUrl,
  ref,
  savedCards = true,
  failedCard = null,
  onReady,
}: {
  sheet: PaymentSheet;
  /** Where the bank sends the customer back to, for the few payment methods that leave the page. */
  returnUrl: string;
  ref: Ref<PaymentFormHandle>;
  savedCards?: boolean;
  /** The card the bank just turned down, so another is offered first. */
  failedCard?: { brand: string | null; last4: string | null } | null;
  onReady?: (ready: boolean) => void;
}) {
  const { config } = useSession();
  const cards = useResource(
    () =>
      savedCards
        ? get<{ data: SavedCard[] }>("/customer/payment-methods")
        : Promise.resolve({ data: [] }),
    [savedCards],
  );
  const [stripe, setStripe] = useState<Stripe | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [choice, setChoice] = useState<string | null>(null);
  const [elementReady, setElementReady] = useState(false);
  const container = useRef<HTMLDivElement>(null);
  const elements = useRef<Elements | null>(null);

  useEffect(() => {
    if (!sheet.publishableKey) {
      setLoadError("Card payments aren't set up. Please choose another way to pay.");
      return;
    }
    let cancelled = false;
    loadStripe(config.stripeJsUrl, sheet.publishableKey)
      .then((loaded) => !cancelled && setStripe(loaded))
      .catch((error: Error) => !cancelled && setLoadError(error.message));
    return () => {
      cancelled = true;
    };
  }, [config.stripeJsUrl, sheet.publishableKey]);

  const saved = cards.data?.data ?? [];
  // A saved card first if there is one (not the one that was just turned down); otherwise a new card.
  useEffect(() => {
    if (cards.loading || choice !== null) return;
    const usable = saved.find(
      (card) => !(card.brand === failedCard?.brand && card.last4 === failedCard?.last4),
    );
    setChoice(usable?.id ?? "new");
  }, [cards.loading, choice, saved, failedCard]);

  // The Payment Element, while "new card" is chosen.
  useEffect(() => {
    if (!stripe || choice !== "new" || !container.current) return;
    setElementReady(false);
    const primary =
      getComputedStyle(document.documentElement).getPropertyValue("--primary").trim() || "#111111";
    const created = stripe.elements({
      clientSecret: sheet.clientSecret,
      appearance: appearance(primary),
    });
    const element: PaymentElement = created.create("payment", { layout: "tabs" });
    element.on("ready", () => setElementReady(true));
    element.mount(container.current);
    elements.current = created;
    return () => {
      element.destroy();
      elements.current = null;
    };
  }, [stripe, choice, sheet.clientSecret]);

  const ready = Boolean(stripe) && choice !== null && (choice !== "new" || elementReady);
  useEffect(() => onReady?.(ready), [ready, onReady]);

  useImperativeHandle(
    ref,
    () => ({
      async confirm() {
        if (!stripe || !choice) return "The payment form isn't ready yet.";
        const result =
          choice === "new"
            ? sheet.mode === "payment"
              ? await stripe.confirmPayment({
                  elements: elements.current!,
                  confirmParams: { return_url: returnUrl },
                  redirect: "if_required",
                })
              : await stripe.confirmSetup({
                  elements: elements.current!,
                  confirmParams: { return_url: returnUrl },
                  redirect: "if_required",
                })
            : sheet.mode === "payment"
              ? await stripe.confirmCardPayment(sheet.clientSecret, { payment_method: choice })
              : await stripe.confirmCardSetup(sheet.clientSecret, { payment_method: choice });
        return result.error ? stripeMessage(result.error) : null;
      },
    }),
    [stripe, choice, sheet, returnUrl],
  );

  if (loadError) return <Notice tone="error">{loadError}</Notice>;
  return (
    <div className="payment-form">
      {saved.length > 0 && (
        <ChoiceGroup label="Card">
          {saved.map((card) => (
            <Choice
              key={card.id}
              name={`card-${sheet.clientSecret}`}
              checked={choice === card.id}
              onSelect={() => setChoice(card.id)}
              icon={<CardIcon />}
              title={`${brandName(card.brand)} •••• ${card.last4}`}
              subtitle={`Expires ${String(card.expMonth).padStart(2, "0")}/${String(card.expYear).slice(-2)}`}
            />
          ))}
          <Choice
            name={`card-${sheet.clientSecret}`}
            checked={choice === "new"}
            onSelect={() => setChoice("new")}
            icon={<PlusIcon />}
            title="Use another card"
          />
        </ChoiceGroup>
      )}
      {choice === "new" && (
        <div className="payment-element-wrap">
          {!elementReady && (
            <div className="payment-element-loading">
              <Spinner small /> Loading the secure card form…
            </div>
          )}
          <div ref={container} className="payment-element" />
        </div>
      )}
      {(!stripe || choice === null) && (
        <div className="payment-element-loading">
          <Spinner small /> Loading…
        </div>
      )}
    </div>
  );
}
